<template>
  <div class="guide">
    <div class="guide__left">
      <SkeletonBlock :is-active="isSkeletonActive">
        <template #skeleton>
          <GuideSkeleton />
        </template>

        <a
          :href="guideUrl"
          @click="sendSoguGuideClickEvent(guide.id, 'icon')"
        >
          <ui-image
            class="guide__img"
            :src="guide.avatar.medium"
            :alt="guide.first_name"
            avatar-size="medium"
          />
        </a>
        <div class="guide__info">
          <a
            class="guide__name"
            :href="guideUrl"
            :data-test="`guide-block-guide-name-${guide.id}`"
            @click="sendSoguGuideClickEvent(guide.id, 'name')"
          >
            {{ guide.first_name }}
          </a>

          <GuidesStatistics :guide="guide" />

          <div
            class="guide__description"
            :class="{
              'description-hover': showMoreButton,
              'show-more-description-height': !showMoreButton,
            }"
            @click.stop="sendSoguGuideClickEvent(guide.id, 'info')"
          >
            <div
              ref="textWrap"
              class="guide__text"
            >
              <span ref="text">{{ guide.description }}</span>
            </div>
            <span
              v-if="showMoreButton"
              class="guide__expand blue-link"
              @click="expandText"
              >{{ t('guide.more') }}</span
            >
          </div>
        </div>
      </SkeletonBlock>
    </div>
    <div class="guide__right">
      <SkeletonBlock :is-active="isSkeletonActive">
        <template #skeleton>
          <SkeletonItem
            :width="buttonSkeletonWidth"
            :height="48"
          ></SkeletonItem>
        </template>

        <ui-button
          :data-test="`guide-btn-to-guide-${guide.id}`"
          :href="guideUrl"
          :text="buttonText"
          @click="sendSoguGuideClickEvent(guide.id, 'experiences')"
        />
      </SkeletonBlock>
    </div>
  </div>
</template>
<script setup lang="ts">
  import GuidesStatistics from '@/entities/guide/ui/GuidesStatistics.vue';
  import { choosePlural, useScreenSize, withoutDomain } from '@experience/shared';
  import { UiImage } from '@experience/core-image-ui';
  import { UiButton } from '@experience/core-button-ui';
  import type { Guide } from '@/shared/api/experience';
  import { useI18n } from '@experience/i18n';
  import ru from '../i18n/ru.json';
  import type { ContextGeoSubject } from '@/shared/types/geography';
  import { trackEvent } from '@/entities/sogu';
  import type { GuideClickType } from '@/entities/guide';
  import { SkeletonBlock, SkeletonItem } from '@experience/core-skeleton-lib';
  import GuideSkeleton from '@/entities/guide/ui/GuideSkeleton.vue';
  import { useListingSkeleton } from '@/shared/composables/useDelayedListingSkeleton.ts';

  type MessageSchema = typeof ru;

  const { isMobile } = useScreenSize();

  const { t } = useI18n<
    {
      message: MessageSchema;
    },
    'ru'
  >({
    inheritLocale: true,
    messages: {
      ru,
    },
  });

  interface Props {
    guide: Guide;
    hasTours?: boolean;
    defaultSoguFields: ContextGeoSubject;
  }

  const props = defineProps<Props>();

  const { isSkeletonActive } = useListingSkeleton();

  const isExpanded = ref(false);
  const text = ref<HTMLElement | null>(null);

  const showMoreButton = computed(() => {
    if (isExpanded.value) {
      return false;
    }
    const textElement = text.value;

    if (textElement) {
      const rects = textElement.getClientRects();

      return rects.length > 3; // Показать "еще" если больше 3 строк
    }

    return false;
  });
  const expandText = () => {
    isExpanded.value = true;
  };

  const buttonText = computed(() => {
    const counter = props.guide[props.hasTours ? 'tours_count_published' : 'exp_count_published'] || 0;

    const offer1 = t('guide.offers_1');
    const offer2 = t('guide.offers_2');
    const offer3 = t('guide.offers_3');

    return counter + ' ' + choosePlural(counter, [offer1, offer2, offer3]);
  });

  const guideUrl = computed(() => withoutDomain(props.guide.url));

  const sendSoguGuideClickEvent = (guideId: number | undefined, click_type: GuideClickType) => {
    const payload = {
      ...props.defaultSoguFields.soguStamp,
      guide_id: guideId,
      click_type,
      page_type: props.hasTours ? 'tours' : 'experiences',
    };
    trackEvent('listing:guides.click', payload);
  };

  const buttonSkeletonWidth = computed(() => {
    if (isMobile.value) {
      return 320;
    }

    return 200;
  });
</script>

<style lang="scss" scoped>
  @use '@experience/design-tokens/typography' as typography;
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  .guide {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 32px;

    * {
      outline: none;
    }

    &:hover {
      .guide__expand {
        color: var(--alias-color-link-default-hovered);
      }
    }

    @include adaptivity.mobile {
      flex-direction: column;
      margin-top: 0;
      padding: 0;
      border: 0;

      &:last-child {
        margin-bottom: 31px;
      }
    }
  }

  .guide__left {
    display: flex;
    flex: 1;

    @include adaptivity.mobile {
      flex-direction: column;
      align-items: center;
    }
  }

  .guide__img {
    display: block;
    width: 120px;
    height: 120px;
    border-radius: 50%;
    pointer-events: none;
  }

  .guide__info {
    display: flex;
    flex: 1;
    flex-direction: column;
    max-width: 700px;
    margin: 0 22px;

    @include adaptivity.mobile {
      margin: 8px 0 16px;
    }
  }

  .guide__name {
    margin-bottom: 8px;
    color: var(--alias-color-text-default);
    text-decoration: none;

    @include typography.base-700-500-extra;

    @include adaptivity.mobile {
      text-align: center;

      @include typography.base-500-400-semi;
    }
  }

  .guide__who {
    margin-bottom: 8px;
    color: var(--alias-color-text-subtle);
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.05em;
    text-decoration: none;
    text-transform: uppercase;

    @include adaptivity.mobile {
      text-align: center;
    }
  }

  .guide__description {
    position: relative;
    overflow: hidden;
    height: 70px;
    transition: height 0.2s linear;

    @include typography.base-400-400-regular;

    @include adaptivity.mobile {
      margin: 6px 0 0;
    }
  }

  .guide__text {
    color: var(--alias-color-text-default);
    line-height: 1.4; /* Убедитесь, что строка имеет четкую высоту */
  }

  .guide__expand {
    position: absolute;
    right: 0;
    bottom: 2px;
    z-index: 2;
    padding: 0 11px 0 45px;
    background: linear-gradient(
      90deg,
      hsla(0deg, 0%, 100%, 0),
      var(--alias-color-background-default) 37%,
      var(--alias-color-background-default) 99%
    );
    text-decoration: none;
    cursor: pointer;

    &:hover {
      color: var(--alias-color-link-default-hovered);
    }
  }

  .guide__right {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    width: 200px;
    text-align: center;

    .button {
      --btn-width: 200px;
    }

    @include adaptivity.mobile {
      width: 100%;

      .button {
        --btn-width: 100%;
      }
    }
  }

  .description-hover {
    cursor: pointer;

    &:hover {
      opacity: 0.8;
    }
  }

  .show-more-padding {
    padding: 0;
  }

  .show-more-description-height {
    height: auto;
  }
</style>
